"use client";

import {
  EllipsisVerticalIcon,
  PencilIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "./catalyst/dropdown";
import { DeleteProductDialog } from "./delete-product-dialog";
import { IconType } from "@icons-pack/react-simple-icons";
import { FlagIcon } from "@heroicons/react/16/solid";
import { ProductType } from "@/lib/entities";
import { useTranslations } from "next-intl";
import { useState, useEffect } from "react";
import { Button } from "./catalyst/button";
import { ReportForm } from "./report-form";

interface ReportContent {
  type: "creator" | "user" | ProductType;
  id: number;
  name: string;
}

interface DeleteEventContent {
  type: ProductType;
  slug: string;
  title: string;
  creatorSlug: string;
}

export type DropdownAction =
  | { type: "edit"; href: string }
  | { type: "report"; content: ReportContent }
  | { type: "view"; href: string }
  | { type: "duplicate"; href: string }
  | { type: "delete"; content: DeleteEventContent };

interface ActionsDropdownProps {
  items: DropdownAction[];
  className?: string;
  testId?: string;
}

export function ActionsDropdown({
  items,
  className,
  testId,
}: Readonly<ActionsDropdownProps>) {
  const t = useTranslations("ActionsDropdown");
  const [mounted, setMounted] = useState(false);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [reportContent, setReportContent] = useState<ReportContent | null>(
    null,
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteContent, setDeleteContent] = useState<DeleteEventContent | null>(
    null,
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);
  }, []);

  // Define icons and labels for each action type
  const ACTION_CONFIG: Record<
    DropdownAction["type"],
    { icon: IconType; label: string; target?: string }
  > = {
    edit: {
      icon: PencilIcon,
      label: t("edit"),
    },
    report: {
      icon: FlagIcon,
      label: t("report"),
    },
    view: {
      icon: EyeIcon,
      label: t("viewPublic"),
      target: "_blank",
    },
    duplicate: {
      icon: DocumentDuplicateIcon,
      label: t("duplicate"),
    },
    delete: {
      icon: TrashIcon,
      label: t("delete"),
    },
  } as const;

  const handleAction = (action: DropdownAction) => {
    if (action.type === "report") {
      setReportContent(action.content);
      setIsReportDialogOpen(true);
    } else if (action.type === "delete") {
      setDeleteContent(action.content);
      setIsDeleteDialogOpen(true);
    }
  };

  return (
    <>
      <div className={className}>
        {mounted ? (
          <Dropdown>
            <DropdownButton
              as={Button}
              outline
              data-testid={testId}
              className="*:data-[slot=icon]:size-5 sm:*:data-[slot=icon]:size-6"
            >
              <EllipsisVerticalIcon data-slot="icon" />
            </DropdownButton>

            <DropdownMenu>
              {items.map((action, index) => {
                const config = ACTION_CONFIG[action.type];
                const Icon = config.icon;
                const href =
                  action.type === "edit" ||
                  action.type === "view" ||
                  action.type === "duplicate"
                    ? action.href
                    : undefined;

                const itemTestId = testId
                  ? `${testId}-item-${action.type}`
                  : `actions-dropdown-item-${action.type}`;

                return (
                  <DropdownItem
                    key={`${action.type}-${index}`}
                    href={href ?? "#"}
                    onClick={href ? undefined : () => handleAction(action)}
                    target={config.target}
                    data-testid={itemTestId}
                  >
                    <Icon />
                    {config.label}
                  </DropdownItem>
                );
              })}
            </DropdownMenu>
          </Dropdown>
        ) : (
          <Button
            outline
            data-testid={testId}
            className="*:data-[slot=icon]:size-5 sm:*:data-[slot=icon]:size-6"
            disabled
          >
            <EllipsisVerticalIcon data-slot="icon" />
          </Button>
        )}
      </div>

      {reportContent && (
        <ReportForm
          contentType={reportContent.type}
          contentId={reportContent.id}
          contentName={reportContent.name}
          open={isReportDialogOpen}
          onSuccess={() => setIsReportDialogOpen(false)}
        />
      )}

      {deleteContent && (
        <DeleteProductDialog
          productType={deleteContent.type}
          productSlug={deleteContent.slug}
          creatorSlug={deleteContent.creatorSlug}
          productTitle={deleteContent.title}
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
        />
      )}
    </>
  );
}
